/**
 * Раздел «Контрагенты» (СПР-1a): справочник внешних организаций и лиц с ролями
 * (заказчик, подрядчик-организация, экспертиза, надзор, поставщик) и людей
 * (эксперты, контакты). Вкладки «Заказчики», «Подрядчики (организации)» (с
 * фильтром по виду), «Экспертизы», «Все», «Люди», «Сотрудники» (ccpm.users, только
 * чтение). Карточка: реквизиты по виду, роли, люди, счётчики связанных ИЛ /
 * аукционов / КП / замечаний. Кнопка «Собрать из ERP» (предпросмотр → применить).
 * Слияние дублей и выбор из справочника в карточках ИЛ/замечаний — СПР-1b.
 * Данные: /api/refs (backend/app/api/refs.py).
 */
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { showNotification } from '../components/notification.js';
import { pickCounterparty, pickPerson } from '../components/ref-picker.js';
import { mountRecordPanel, openRecordPanel, closeRecordPanel } from '../components/record-panel.js';
import {
  KINDS, ROLES, PERSON_ROLES, KIND_LABELS, ROLE_LABELS, PERSON_ROLE_LABELS,
  kindLabel, rolesLabel, fieldsForKind, innHint, validateInnClient, collectSummary,
} from '../utils/counterparties-format.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';
const clip = (s) => `<span class="dt-clip" title="${esc(s ?? '')}">${esc(s || '—')}</span>`;

const TABS = [
  { key: 'customers', label: 'Заказчики', role: 'customer' },
  { key: 'contractors', label: 'Подрядчики (организации)', role: 'contractor', kinds: true },
  { key: 'expertise', label: 'Экспертизы', role: 'expertise' },
  { key: 'all', label: 'Все', roleFilter: true, kinds: true },
  { key: 'people', label: 'Люди' },
  { key: 'employees', label: 'Сотрудники' },
];

let _root = null;
let _mode = 'customers';
let _table = null;

export async function initCounterparties(container) {
  _table = null;
  _mode = 'customers';
  container.innerHTML = `
    <div class="counterparties-view tenders-view">
      <header class="tenders-header">
        <h2>Контрагенты</h2>
        <div class="tenders-modes" role="tablist">
          ${TABS.map(t => `<button type="button" role="tab" class="tenders-mode" data-cp-tab="${t.key}">${esc(t.label)}</button>`).join('')}
        </div>
        <span class="tenders-hint">Внешние организации и лица ERP. «Подрядчики» в разделе CCPM — это исполнители для Telegram-бота, другая сущность.</span>
      </header>
      <div class="counterparties-body tenders-body"></div>
    </div>`;
  _root = container.querySelector('.counterparties-view');
  // Открытая правая панель записи (УИ-2c) сжимает таблицу, а не закрывает её
  // (переиспользуем --tdi-space из tenders.css, как expertise.js:59 / normcontrol.js:122).
  _root.addEventListener('ccpm:inspector-resize', (e) => {
    _root.style.setProperty('--tdi-space', `${Math.round(e.detail.width)}px`);
  });
  mountRecordPanel(_root, { key: 'counterparties', defaultWidth: 520 });
  _root.addEventListener('click', onTabClick);
  setMode('customers', true);
}

function onTabClick(e) {
  const tab = e.target.closest?.('[data-cp-tab]');
  if (tab) { e.preventDefault(); setMode(tab.dataset.cpTab); return; }
  const body = _root.querySelector('.counterparties-body');
  if (e.target.closest?.('[data-cp-new]')) { e.preventDefault(); openCounterpartyPanel(null); }
  else if (e.target.closest?.('[data-cp-collect]')) { e.preventDefault(); openCollect(); }
  else if (e.target.closest?.('[data-cp-match]')) { e.preventDefault(); openMatchContacts(); }
  else if (e.target.closest?.('[data-cp-person-new]')) { e.preventDefault(); openPersonPanel(null); }
  else if (e.target.closest?.('[data-cp-admin]')) { /* ссылка data-route обработает роутер */ }
  if (!body) return;
}

function setMode(mode, force = false) {
  if (mode === _mode && !force && _root.querySelector('.counterparties-body').children.length) return;
  closeRecordPanel();   // несохранённые правки при смене вкладки закрываем молча (УИ-2c)
  _mode = mode;
  _table = null;
  _root.querySelectorAll('.tenders-mode').forEach(b => {
    b.classList.toggle('is-active', b.dataset.cpTab === mode);
    b.setAttribute('aria-selected', String(b.dataset.cpTab === mode));
  });
  const body = _root.querySelector('.counterparties-body');
  body.innerHTML = '';
  const tab = TABS.find(t => t.key === mode);
  if (mode === 'people') renderPeople(body);
  else if (mode === 'employees') renderEmployees(body);
  else renderCounterpartyTab(body, tab);
}

// ── Вкладки контрагентов ───────────────────────────────────────────────────────

const CP_COLUMNS = [
  { key: 'name', label: 'Наименование', width: 280,
    render: r => `<span class="dt-clip" title="${esc(r.full_name || r.name)}">${esc(r.name)}</span>` },
  { key: 'kind', label: 'Вид', width: 120,
    render: r => esc(kindLabel(r.kind)) + (r.org_form ? ` ${esc(r.org_form)}` : '') },
  { key: 'inn', label: 'ИНН', width: 150,
    render: r => (r.inn
      ? esc(r.inn) + (r.inn_valid === false ? ' <span class="cp-warn" title="ИНН не прошёл контрольную сумму">⚠</span>' : '')
      : '—') },
  { key: 'roles', label: 'Роли', width: 240, render: r => clip(rolesLabel(r.roles)) },
  { key: 'phone', label: 'Телефон', width: 140, render: r => esc(r.phone || '—') },
  { key: 'email', label: 'Email', width: 180, render: r => esc(r.email || '—') },
  { key: 'is_active', label: 'Активен', width: 84, align: 'center', render: r => (r.is_active ? 'да' : 'нет') },
];

function renderCounterpartyTab(body, tab) {
  const el = document.createElement('div');
  body.appendChild(el);
  if (tab.kinds) {
    const hint = document.createElement('p');
    hint.className = 'cp-tab-hint';
    hint.innerHTML = tab.role === 'contractor'
      ? 'Подрядчики-организации справочника. Исполнители для Telegram-бота — в разделе CCPM → <a class="dt-link" href="/contractor-updates" data-route="/contractor-updates">Подрядчики</a>.'
      : 'Все контрагенты справочника с их ролями и видами.';
    body.insertBefore(hint, el);
  }

  const filters = [];
  if (tab.roleFilter) {
    filters.push({ key: 'role', label: 'Роль', multi: true,
      options: ROLES.map(v => ({ value: v, label: ROLE_LABELS[v] })) });
  }
  if (tab.kinds) {
    filters.push({ key: 'kind', label: 'Вид', multi: true,
      options: KINDS.map(v => ({ value: v, label: KIND_LABELS[v] })) });
  }

  _table = createDataTable(el, {
    storageKey: `ccpm_dt_cp_${tab.key}`,
    columns: CP_COLUMNS,
    filters,
    closedToggle: { label: 'только активные' },
    actions: true,
    search: { placeholder: 'Поиск по названию / ИНН' },
    pageSizes: [50, 100, 200],
    emptyHint: 'Контрагентов пока нет. Создайте вручную или нажмите «Собрать из ERP».',
    rowId: r => r.id,
    onRowClick: r => openCounterpartyPanel(r.id),
    fetch: async ({ limit, offset, q, filters: f, hideClosed }) => {
      const params = { limit, offset };
      if (q) params.q = q;
      const roles = tab.role ? [tab.role] : (f.role ?? []);
      if (roles.length) params.role = roles.join(',');
      if (f.kind?.length) params.kind = f.kind.join(',');
      if (hideClosed) params.active = true;
      return api.refs.counterparties(params);
    },
    facets: async ({ q, filters: f, hideClosed }) => {
      const params = {};
      if (q) params.q = q;
      const roles = tab.role ? [tab.role] : (f.role ?? []);
      if (roles.length) params.role = roles.join(',');
      if (f.kind?.length) params.kind = f.kind.join(',');
      if (hideClosed) params.active = true;
      const res = await api.refs.counterpartyFacets(params);
      return { counts: res.counts, summary: res.summary };
    },
  });
  _table.setActions(`
    ${canDo('ref.merge') ? '<button type="button" class="dt-btn cp-primary" data-cp-collect>Собрать из ERP</button>' : ''}
    ${canDo('ref.write') ? '<button type="button" class="dt-btn cp-primary" data-cp-new>Создать контрагента</button>' : ''}
  `);
}

// ── Люди ────────────────────────────────────────────────────────────────────

const PERSON_COLUMNS = [
  { key: 'fio', label: 'ФИО', width: 220, render: r => esc(r.fio) },
  { key: 'position', label: 'Должность', width: 170, render: r => esc(r.position || '—') },
  { key: 'counterparty_name', label: 'Контрагент', width: 220, render: r => clip(r.counterparty_name) },
  { key: 'roles', label: 'Роли', width: 170, render: r => esc(rolesLabel(r.roles, PERSON_ROLE_LABELS)) },
  { key: 'phone', label: 'Телефон', width: 140, render: r => esc(r.phone || '—') },
  { key: 'email', label: 'Email', width: 180, render: r => esc(r.email || '—') },
];

function renderPeople(body) {
  const el = document.createElement('div');
  body.appendChild(el);
  _table = createDataTable(el, {
    storageKey: 'ccpm_dt_cp_people',
    columns: PERSON_COLUMNS,
    filters: [{ key: 'role', label: 'Роль', multi: true,
      options: PERSON_ROLES.map(v => ({ value: v, label: PERSON_ROLE_LABELS[v] })) }],
    closedToggle: { label: 'только активные' },
    actions: true,
    search: { placeholder: 'Поиск по ФИО / email / телефону' },
    emptyHint: 'Людей пока нет. Создайте вручную или соберите экспертов из замечаний.',
    rowId: r => r.id,
    onRowClick: r => openPersonPanel(r),
    fetch: async ({ limit, offset, q, filters: f, hideClosed }) => {
      const params = { limit, offset };
      if (q) params.q = q;
      if (f.role?.length) params.role = f.role.join(',');
      if (hideClosed) params.active = true;
      return api.refs.persons(params);
    },
  });
  _table.setActions(`
    ${canDo('ref.merge') ? '<button type="button" class="dt-btn" data-cp-match>Сопоставить контакты</button>' : ''}
    ${canDo('ref.write') ? '<button type="button" class="dt-btn cp-primary" data-cp-person-new>Создать человека</button>' : ''}
  `);
}

// ── Сотрудники (ccpm.users, только чтение) ─────────────────────────────────────

const ROLE_RU = { owner: 'Собственник', admin: 'Админ', manager: 'Менеджер', user: 'Пользователь' };

async function renderEmployees(body) {
  body.innerHTML = '<div class="loading-spinner">Загрузка...</div>';
  let rows;
  try { rows = await api.refs.employees(); }
  catch (e) { body.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
  body.innerHTML = `
    <p class="cp-tab-hint">Сотрудники ПКБ (ccpm.users). Только чтение — создание и правка в разделе
      <a class="dt-link" href="/admin" data-route="/admin">Админ</a>.</p>
    <table class="dt-table cp-employees">
      <thead><tr><th>ФИО</th><th>Логин</th><th>Роль</th><th>Email</th><th>Активен</th></tr></thead>
      <tbody>${rows.map(u => `<tr>
        <td>${esc(u.full_name || '—')}</td><td>${esc(u.login)}</td>
        <td>${esc(ROLE_RU[u.role] || u.role)}</td><td>${esc(u.email || '—')}</td>
        <td>${u.active ? 'да' : 'нет'}</td></tr>`).join('')}</tbody>
    </table>`;
}

// ── Диалог (общий) ─────────────────────────────────────────────────────────────

function openDialog({ title, bodyHtml, confirmLabel = 'Сохранить', wide = false, grid = false, onMount, onConfirm }) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'app-modal cp-modal' + (wide ? ' cp-modal-wide' : '');
    dlg.innerHTML = `
      <form method="dialog" class="modal-form">
        <h3 class="modal-title">${esc(title)}</h3>
        <div class="cp-dialog-body${grid ? ' is-grid' : ''}">${bodyHtml}</div>
        <div class="modal-actions">
          <button type="button" class="modal-cancel">Отмена</button>
          ${onConfirm ? `<button type="submit" class="modal-confirm">${esc(confirmLabel)}</button>` : ''}
        </div>
      </form>`;
    document.body.appendChild(dlg);
    const close = (v) => { try { dlg.close(); } catch { /* уже закрыт */ } dlg.remove(); resolve(v); };
    dlg.querySelector('.modal-cancel').addEventListener('click', () => close(null));
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(null); });
    dlg.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!onConfirm) { close(null); return; }
      const btn = dlg.querySelector('.modal-confirm');
      btn.disabled = true;
      const res = await onConfirm(dlg);
      btn.disabled = false;
      if (res !== false) close(res);
    });
    onMount?.(dlg, close);
    dlg.showModal();
  });
}

const field = (name, label, value, attrs = '') =>
  `<label class="modal-field"><span class="modal-field-label">${esc(label)}</span>
     <input name="${name}" value="${esc(value ?? '')}" ${attrs} /></label>`;

// ── Форма контрагента ──────────────────────────────────────────────────────────

function counterpartyFormHtml(cp) {
  const kind = cp?.kind ?? 'legal';
  const roles = new Set(cp?.roles ?? []);
  return `
    <label class="modal-field"><span class="modal-field-label">Вид</span>
      <select name="kind" class="modal-select" data-cp-kind>
        ${KINDS.map(k => `<option value="${k}"${k === kind ? ' selected' : ''}>${esc(KIND_LABELS[k])}</option>`).join('')}
      </select></label>
    ${field('name', 'Наименование (краткое)', cp?.name, 'required')}
    ${field('full_name', 'Наименование полное', cp?.full_name)}
    <div class="cp-field" data-when="orgForm">${field('org_form', 'ОПФ (ООО, АО…)', cp?.org_form, 'placeholder="подставится из названия"')}</div>
    <label class="modal-field"><span class="modal-field-label">ИНН <span class="cp-inn-hint">${esc(innHint(kind))}</span></span>
      <input name="inn" value="${esc(cp?.inn ?? '')}" inputmode="numeric" data-cp-inn />
      <span class="cp-inn-state" data-cp-inn-state></span></label>
    <div class="cp-field" data-when="kpp">${field('kpp', 'КПП', cp?.kpp)}</div>
    <div class="cp-field" data-when="ogrn">${field('ogrn', 'ОГРН / ОГРНИП', cp?.ogrn)}</div>
    <div class="cp-field" data-when="snils">${field('snils', 'СНИЛС (необязательно)', cp?.snils)}</div>
    <fieldset class="cp-roles"><legend>Роли</legend>
      ${ROLES.map(r => `<label class="cp-check"><input type="checkbox" name="role" value="${r}"${roles.has(r) ? ' checked' : ''}> ${esc(ROLE_LABELS[r])}</label>`).join('')}
    </fieldset>
    ${field('phone', 'Телефон', cp?.phone)}
    ${field('email', 'Email', cp?.email, 'type="email"')}
    ${field('website', 'Сайт', cp?.website)}
    ${field('address', 'Адрес', cp?.address)}
    <label class="modal-field"><span class="modal-field-label">Примечание</span>
      <textarea name="note" rows="2" class="modal-textarea">${esc(cp?.note ?? '')}</textarea></label>
    ${cp ? `<label class="cp-check"><input type="checkbox" name="is_active"${cp.is_active ? ' checked' : ''}> Активен</label>` : ''}`;
}

function applyKindFields(scope) {
  const kind = scope.querySelector('[data-cp-kind]').value;
  const f = fieldsForKind(kind);
  scope.querySelectorAll('.cp-field[data-when]').forEach(el => {
    el.hidden = !f[el.dataset.when];
  });
  const hint = scope.querySelector('.cp-inn-hint');
  if (hint) hint.textContent = innHint(kind);
  checkInn(scope);
}

function checkInn(scope) {
  const inp = scope.querySelector('[data-cp-inn]');
  const state = scope.querySelector('[data-cp-inn-state]');
  if (!inp || !state) return;
  const v = inp.value.replace(/\D/g, '');
  if (!v) { state.textContent = ''; state.className = 'cp-inn-state'; return; }
  const ok = validateInnClient(v);
  state.textContent = ok ? '✓ контрольная сумма верна' : '⚠ не проходит проверку (сохранится с пометкой)';
  state.className = 'cp-inn-state ' + (ok ? 'is-ok' : 'is-warn');
}

/** Собрать тело контрагента из формы панели (как прежний onConfirm диалога). */
function collectCounterparty(form) {
  const val = (n) => form.elements[n]?.value.trim() || null;
  const body = {
    name: val('name'), full_name: val('full_name'), kind: form.elements.kind.value,
    org_form: val('org_form'), inn: val('inn'), kpp: val('kpp'), ogrn: val('ogrn'),
    snils: val('snils'), phone: val('phone'), email: val('email'),
    website: val('website'), address: val('address'), note: val('note'),
    roles: [...form.querySelectorAll('input[name="role"]:checked')].map(c => c.value),
  };
  const act = form.elements.is_active;   // чекбокс есть только у существующей записи
  if (act) body.is_active = act.checked;
  return body;
}

// ── Контрагент: правая панель (УИ-2c) ───────────────────────────────────────────
// Было: openCard (просмотр) + openForm (правка) в центральных <dialog>. Теперь —
// одна правая панель (components/record-panel.js, render-body): форма контрагента
// (набор полей зависит от «Вида», живая проверка ИНН) + ниже, у существующей
// записи, read-only блок «Связанные записи»/«Люди»/«Источник». Save/Отмена — в
// футере панели; «Объединить с…» (ref.merge) — доп. кнопка футера, pickCounterparty
// открывается ПОВЕРХ панели. Слияние/подтверждения по-прежнему модалки.

const COUNTER_LINKS = [
  { field: 'sheet_count', label: 'Исполнительные листы', route: '/projects/sheets' },
  { field: 'tender_count', label: 'Аукционы', route: '/tenders' },
  { field: 'quote_count', label: 'КП и заявки', route: '/sales/quotes' },
  { field: 'remark_count', label: 'Замечания экспертизы', route: '/projects/expertise' },
];

const SOURCE_LABELS = { manual: 'вручную', onec: '1С', auto: 'автосбор' };

/** Read-only блок под формой существующего контрагента: счётчики + люди + источник. */
function relatedBlockHtml(cp, people) {
  const counters = COUNTER_LINKS.map(c => `
    <a class="cp-counter" href="${c.route}" data-route="${c.route}">
      <span class="cp-counter-n">${cp[c.field] ?? 0}</span>
      <span class="cp-counter-l">${esc(c.label)}</span></a>`).join('');
  const peopleHtml = people.length
    ? `<ul class="cp-people">${people.map(p => `<li>${esc(p.fio)}${p.position ? ` — ${esc(p.position)}` : ''}
        ${p.roles?.length ? ` <span class="cp-muted">(${esc(rolesLabel(p.roles, PERSON_ROLE_LABELS))})</span>` : ''}</li>`).join('')}</ul>`
    : '<p class="cp-muted">Людей не привязано.</p>';
  return `
    <div class="cp-card cp-panel-related">
      <div class="cp-card-section"><h4>Связанные записи</h4><div class="cp-counters">${counters}</div></div>
      <div class="cp-card-section"><h4>Люди</h4>${peopleHtml}</div>
      <div class="cp-card-section cp-muted">Источник: ${esc(SOURCE_LABELS[cp.source] ?? cp.source)}</div>
      ${!cp.is_active ? '<p class="cp-warn-row">Контрагент деактивирован.</p>' : ''}
    </div>`;
}

/** Открыть контрагента в правой панели. id=null — создание. */
async function openCounterpartyPanel(id) {
  const ro = !canDo('ref.write');
  let cp = null;
  let people = [];
  if (id != null) {
    try {
      cp = await api.refs.counterparty(id);
      people = (await api.refs.persons({ counterparty_id: id, limit: 200 })).items;
    } catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  }
  openRecordPanel(cp, {
    title: (rec) => (rec ? rec.name : 'Новый контрагент'),
    readonly: ro,
    saveLabel: cp ? 'Сохранить' : 'Создать',
    onClose: () => _table?.setSelected(null),
    footerActions: (!ro && cp && canDo('ref.merge')) ? [{ key: 'merge', label: 'Объединить с…' }] : [],
    onAction: (key, rec) => { if (key === 'merge') openMergeCounterparty(rec); },
    collect: (form) => collectCounterparty(form),
    onSave: (body, { isCreate, record }) => {
      if (!body.name) throw new Error('Укажите наименование');
      return isCreate ? api.refs.createCounterparty(body) : api.refs.patchCounterparty(record.id, body);
    },
    reload: (saved) => api.refs.counterparty(saved.id),
    onSaved: (_fresh, { isCreate }) => {
      showNotification(isCreate ? 'Контрагент создан' : 'Контрагент сохранён', 'success');
      _table?.reload();
    },
    render: (rec, ctx) => {
      ctx.host.innerHTML = `
        <form class="record-panel-form modal-grid" data-rp-form novalidate>
          ${counterpartyFormHtml(rec)}
        </form>
        ${rec ? relatedBlockHtml(rec, people) : ''}`;
      const form = ctx.host.querySelector('[data-rp-form]');
      applyKindFields(form);   // спрятать неприменимые к «Виду» поля даже в read-only
      if (ro) {
        form.querySelectorAll('input, select, textarea').forEach(el => { el.disabled = true; });
        return;
      }
      form.querySelector('[data-cp-kind]').addEventListener('change', () => applyKindFields(form));
      form.querySelector('[data-cp-inn]').addEventListener('input', () => checkInn(form));
      // клик по счётчику-ссылке уводит на другой раздел — закрываем панель (роутер по data-route)
      ctx.host.querySelectorAll('[data-route]').forEach(a => a.addEventListener('click', () => closeRecordPanel()));
    },
  });
  _table?.setSelected(cp?.id ?? null);
}

// ── Человек: правая панель (УИ-2c) ──────────────────────────────────────────────

/** Собрать тело человека из формы панели. */
function collectPerson(form) {
  const val = (n) => form.elements[n]?.value.trim() || null;
  const cpId = form.elements.counterparty_id.value;
  const body = {
    fio: val('fio'), position: val('position'),
    counterparty_id: cpId ? Number(cpId) : null,
    phone: val('phone'), email: val('email'), note: val('note'),
    roles: [...form.querySelectorAll('input[name="role"]:checked')].map(c => c.value),
  };
  const act = form.elements.is_active;
  if (act) body.is_active = act.checked;
  return body;
}

function personFormHtml(p, cps) {
  const roles = new Set(p?.roles ?? []);
  return `
    ${field('fio', 'ФИО', p?.fio, 'required')}
    ${field('position', 'Должность', p?.position)}
    <label class="modal-field"><span class="modal-field-label">Контрагент (организация)</span>
      <select name="counterparty_id" class="modal-select">
        <option value="">— не задан</option>
        ${cps.map(c => `<option value="${c.id}"${p?.counterparty_id === c.id ? ' selected' : ''}>${esc(c.name)}</option>`).join('')}
      </select></label>
    <fieldset class="cp-roles"><legend>Роли</legend>
      ${PERSON_ROLES.map(r => `<label class="cp-check"><input type="checkbox" name="role" value="${r}"${roles.has(r) ? ' checked' : ''}> ${esc(PERSON_ROLE_LABELS[r])}</label>`).join('')}
    </fieldset>
    ${field('phone', 'Телефон', p?.phone)}
    ${field('email', 'Email', p?.email, 'type="email"')}
    <label class="modal-field modal-field-wide"><span class="modal-field-label">Примечание</span>
      <textarea name="note" rows="2" class="modal-textarea">${esc(p?.note ?? '')}</textarea></label>
    ${p ? `<label class="cp-check"><input type="checkbox" name="is_active"${p.is_active ? ' checked' : ''}> Активен</label>` : ''}`;
}

/** Открыть человека в правой панели. p=null — создание. */
async function openPersonPanel(p) {
  const ro = !canDo('ref.write');
  // список контрагентов для выбора (небольшой справочник — грузим все активные)
  let cps = [];
  try { cps = (await api.refs.counterparties({ active: true, limit: 500 })).items; } catch { /* пусто */ }
  openRecordPanel(p ?? null, {
    title: (rec) => (rec?.fio ? rec.fio : 'Новый человек'),
    readonly: ro,
    saveLabel: p ? 'Сохранить' : 'Создать',
    onClose: () => _table?.setSelected(null),
    footerActions: (!ro && p && canDo('ref.merge')) ? [{ key: 'merge', label: 'Объединить с…' }] : [],
    onAction: (key, rec) => { if (key === 'merge') openMergePerson(rec); },
    collect: (form) => collectPerson(form),
    onSave: (body, { isCreate, record }) => {
      if (!body.fio) throw new Error('Укажите ФИО');
      return isCreate ? api.refs.createPerson(body) : api.refs.patchPerson(record.id, body);
    },
    reload: (saved) => api.refs.person(saved.id),
    onSaved: (_fresh, { isCreate }) => {
      showNotification(isCreate ? 'Человек создан' : 'Человек сохранён', 'success');
      _table?.reload();
    },
    render: (rec, ctx) => {
      ctx.host.innerHTML = `
        <form class="record-panel-form modal-grid" data-rp-form novalidate>
          ${personFormHtml(rec, cps)}
        </form>`;
      if (ro) {
        ctx.host.querySelectorAll('input, select, textarea').forEach(el => { el.disabled = true; });
      }
    },
  });
  _table?.setSelected(p?.id ?? null);
}

// ── Автосбор из ERP ──────────────────────────────────────────────────────────

function collectReportHtml(res) {
  const list = (title, items) => (items?.length
    ? `<div class="cp-card-section"><h4>${esc(title)} (${items.length})</h4>
         <ul class="cp-report-list">${items.slice(0, 40).map(s => `<li>${esc(s)}</li>`).join('')}</ul></div>`
    : '');
  const fk = Object.entries(res.fk_set ?? {});
  return `
    <div class="cp-card">
      <p class="cp-report-summary">${esc(collectSummary(res))}</p>
      ${fk.length ? `<div class="cp-card-section"><h4>Проставлено связей</h4>
        <ul class="cp-report-list">${fk.map(([k, n]) => `<li>${esc(k)}: ${n}</li>`).join('')}</ul></div>` : ''}
      ${list('Создано и найдено (примеры)', res.examples)}
      ${list('Сомнительное (сходится с несколькими — не слито)', res.ambiguous)}
      ${list('ИНН не прошёл проверку (сохранён с пометкой)', res.invalid_inn)}
    </div>`;
}

async function openCollect() {
  let preview;
  try { preview = await api.refs.collect(false); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  const applied = await openDialog({
    title: 'Собрать контрагентов из ERP — предпросмотр',
    wide: true,
    confirmLabel: 'Применить',
    bodyHtml: collectReportHtml(preview) + '<p class="cp-muted">«Применить» запишет контрагентов, людей и проставит связи.</p>',
    onConfirm: async (dlg) => {
      try {
        const res = await api.refs.collect(true);
        // показать итог применения прямо в диалоге перед закрытием
        dlg.querySelector('.cp-dialog-body').innerHTML = collectReportHtml(res)
          + '<p class="cp-inn-state is-ok">Готово.</p>';
        showNotification(collectSummary(res), 'success');
        return res;
      } catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    },
  });
  if (applied) _table?.reload();
}

// ── Слияние дублей (СПР-1b) ────────────────────────────────────────────────────

const FK_LABELS = {
  'tenders.customer_id': 'аукционы (заказчик)',
  'quotes.customer_id': 'КП (заказчик)',
  'project_sheets.counterparty_id': 'исполнительные листы (заказчик)',
  'expertise_remarks.expertise_org_id': 'замечания (организация экспертизы)',
  'expertise_files.expertise_org_id': 'письма экспертизы (организация)',
  'persons.counterparty_id': 'люди (контрагент)',
  'project_sheet_contacts.person_id': 'контакты листов',
  'tender_contacts.person_id': 'контакты аукционов',
};

function mergePreviewHtml(res) {
  const moved = Object.entries(res.moved ?? {});
  const total = moved.reduce((s, [, n]) => s + n, 0);
  return `
    <div class="cp-card">
      <p class="cp-report-summary">«${esc(res.from_name)}» будет присоединён к «${esc(res.into_name)}» и удалён.</p>
      ${res.inn_conflict ? '<p class="cp-warn-row">У записей разные корректные ИНН — объединять нельзя.</p>' : ''}
      <div class="cp-card-section"><h4>Перейдёт связей: ${total}</h4>
        ${moved.length ? `<ul class="cp-report-list">${moved.map(([k, n]) =>
          `<li>${esc(FK_LABELS[k] || k)}: ${n}</li>`).join('')}</ul>` : '<p class="cp-muted">Связей нет.</p>'}</div>
      ${res.roles_added?.length ? `<div class="cp-card-section"><h4>Добавятся роли</h4>
        <p>${esc(rolesLabel(res.roles_added))}</p></div>` : ''}
      ${res.filled?.length ? `<div class="cp-card-section"><h4>Дополнятся пустые реквизиты</h4>
        <p>${esc(res.filled.join(', '))}</p></div>` : ''}
    </div>`;
}

async function runMerge(preview, applyFn) {
  const applied = await openDialog({
    title: 'Объединение дублей — предпросмотр',
    wide: true,
    confirmLabel: preview.inn_conflict ? '' : 'Объединить',
    bodyHtml: mergePreviewHtml(preview)
      + (preview.inn_conflict ? '' : '<p class="cp-muted">«Объединить» перенесёт связи и удалит дубль без возможности отката.</p>'),
    onConfirm: preview.inn_conflict ? null : async () => {
      try {
        const res = await applyFn();
        showNotification('Дубль объединён', 'success');
        return res;
      } catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    },
  });
  if (applied) _table?.reload();
}

async function openMergeCounterparty(main) {
  const dup = await pickCounterparty({
    title: `Объединить «${main.name}» — выберите дубль`, allowCreate: false,
  });
  if (!dup || dup.action !== 'select') return;
  if (dup.id === main.id) { showNotification('Нельзя объединить запись саму с собой', 'error'); return; }
  let preview;
  try { preview = await api.refs.mergeCounterparty(dup.id, main.id, false); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  await runMerge(preview, () => api.refs.mergeCounterparty(dup.id, main.id, true));
}

async function openMergePerson(main) {
  const dup = await pickPerson({ title: `Объединить «${main.fio}» — выберите дубль` });
  if (!dup || dup.action !== 'select') return;
  if (dup.id === main.id) { showNotification('Нельзя объединить запись саму с собой', 'error'); return; }
  let preview;
  try { preview = await api.refs.mergePerson(dup.id, main.id, false); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  await runMerge(preview, () => api.refs.mergePerson(dup.id, main.id, true));
}

// ── Сопоставление контактов со справочником «Люди» (СПР-1b) ──────────────────────

function matchReportHtml(res) {
  const linked = Object.entries(res.linked ?? {});
  const total = linked.reduce((s, [, n]) => s + n, 0);
  return `
    <div class="cp-card">
      <p class="cp-report-summary">${res.apply ? 'Связано' : 'Будет связано'} контактов: ${total};
        людей: найдено ${res.persons_matched}, создано ${res.persons_created}.</p>
      ${linked.length ? `<div class="cp-card-section"><h4>Связи</h4>
        <ul class="cp-report-list">${linked.map(([k, n]) =>
          `<li>${esc(FK_LABELS[k] || k)}: ${n}</li>`).join('')}</ul></div>` : ''}
      ${res.examples?.length ? `<div class="cp-card-section"><h4>Примеры</h4>
        <ul class="cp-report-list">${res.examples.map(s => `<li>${esc(s)}</li>`).join('')}</ul></div>` : ''}
      ${res.ambiguous?.length ? `<div class="cp-card-section"><h4>Сомнительное (несколько совпадений — не связали)</h4>
        <ul class="cp-report-list">${res.ambiguous.map(s => `<li>${esc(s)}</li>`).join('')}</ul></div>` : ''}
    </div>`;
}

async function openMatchContacts() {
  let preview;
  try { preview = await api.refs.matchContacts(false); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  await openDialog({
    title: 'Сопоставить контакты с людьми — предпросмотр',
    wide: true,
    confirmLabel: 'Применить',
    bodyHtml: matchReportHtml(preview)
      + '<p class="cp-muted">«Применить» свяжет контакты с людьми и создаст недостающих.</p>',
    onConfirm: async (dlg) => {
      try {
        const res = await api.refs.matchContacts(true);
        dlg.querySelector('.cp-dialog-body').innerHTML = matchReportHtml(res)
          + '<p class="cp-inn-state is-ok">Готово.</p>';
        showNotification('Контакты сопоставлены', 'success');
        return res;
      } catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    },
  });
}
